import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ProfileService from "../../services/profile.service";


/* =====================================================
   GET PROFILE
===================================================== */

export const getProfile = createAsyncThunk(
  "profile/getProfile",
  async (params, thunkAPI) => {
    try {

      const response =
        await ProfileService.getProfile(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch profile"
      );

    }
  }
);


/* =====================================================
   GET DASHBOARD SUMMARY
===================================================== */

export const getDashboardSummary = createAsyncThunk(
  "profile/getDashboardSummary",
  async (params, thunkAPI) => {
    try {

      const response =
        await ProfileService.getDashboardSummary(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch dashboard summary"
      );

    }
  }
);


export const getMyTeamList = createAsyncThunk(
  "profile/getMyTeamList",
  async (params, thunkAPI) => {
    try {

      const response =
        await ProfileService.getMyTeamList(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch dashboard summary"
      );

    }
  }
);


/* =====================================================
   GET DIRECT REFERRALS
===================================================== */

export const getDirectReferrals = createAsyncThunk(
  "profile/getDirectReferrals",
  async (params, thunkAPI) => {
    try {

      const response =
        await ProfileService.getDirectReferrals(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch direct referrals"
      );

    }
  }
);


/* =====================================================
   GET QR
===================================================== */

export const getQr = createAsyncThunk(
  "profile/getQr",
  async (params, thunkAPI) => {
    try {

      const response =
        await ProfileService.getQr(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch QR"
      );

    }
  }
);


/* =====================================================
   GET MY REFERRAL LEVELS
===================================================== */

export const getMyReferralLevels = createAsyncThunk(
  "profile/getMyReferralLevels",
  async (params, thunkAPI) => {

    try {

      const response =
        await ProfileService.getMyReferralLevels(params);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch referral levels"
      );

    }

  }
);


/* =====================================================
   UPDATE PROFILE
===================================================== */

export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (payload, thunkAPI) => {

    try {

      const response =
        await ProfileService.editProfile(payload);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to update profile"
      );

    }

  }
);


/* =====================================================
   CHANGE PASSWORD
===================================================== */

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async (payload, thunkAPI) => {

    try {

      const response =
        await ProfileService.changePassword(payload);

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to change password"
      );

    }

  }
);


/* =====================================================
   SUBMIT JOINING PAYMENT
===================================================== */

export const submitJoiningPayment = createAsyncThunk(
  "profile/submitJoiningPayment",
  async (payload, thunkAPI) => {

    try {

      const response =
        await ProfileService.submitJoiningPayment(
          payload
        );

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit joining payment"
      );

    }

  }
);


/* =====================================================
   INITIAL STATE
===================================================== */

const initialState = {

  /* ================= COMMON ================= */

  loading: false,

  error: null,


  /* ================= PROFILE ================= */

  profilelist: {},


  /* ================= DASHBOARD ================= */

  dashboardSummary: {},

  getDashboardSummaryLoading: false,


  /* ================= DIRECT REFERRALS ================= */

  directReferrals: [],

  directReferralsLoading: false,

  directReferralsCount: 0,


  /* ================= MY REFERRAL LEVELS ================= */

  myReferralLevels: {},
  myReferralLevelsLoading: false,
  myReferralLevelsCount: 0,
  myTeamError: null,

  /* ================= QR ================= */

  qrData: null,

  qrLoading: false,


  /* ================= JOINING PAYMENT ================= */

  joiningPaymentLoading: false,

  joiningPaymentSuccess: false,

  joiningPaymentData: null,


  /* ================= UPDATE PROFILE ================= */

  updateSuccess: false,

};


/* =====================================================
   SLICE
===================================================== */

const profileSlice = createSlice({

  name: "profile",

  initialState,

  reducers: {


    /* =================================================
       RESET UPDATE STATUS
    ================================================= */

    resetUpdateStatus: (state) => {

      state.updateSuccess = false;

    },


    /* =================================================
       RESET JOINING PAYMENT STATUS
    ================================================= */

    resetJoiningPaymentStatus: (state) => {

      state.joiningPaymentSuccess = false;

      state.joiningPaymentData = null;

      state.error = null;

    },


    /* =================================================
       CLEAR PROFILE ERROR
    ================================================= */

    clearProfileError: (state) => {

      state.error = null;

    },


  },


  /* =====================================================
     EXTRA REDUCERS
  ===================================================== */

  extraReducers: (builder) => {

    builder


      /* =================================================
         GET PROFILE
      ================================================= */

      .addCase(
        getProfile.pending,
        (state) => {

          state.loading = true;

          state.error = null;

        }
      )

      .addCase(
        getProfile.fulfilled,
        (state, action) => {

          state.loading = false;

          state.profilelist =
            action.payload?.data || {};

        }
      )

      .addCase(
        getProfile.rejected,
        (state, action) => {

          state.loading = false;

          state.error =
            action.payload ||
            "Failed to fetch profile";

        }
      )


      /* =================================================
         GET DASHBOARD SUMMARY
      ================================================= */

      .addCase(
        getDashboardSummary.pending,
        (state) => {

          state.getDashboardSummaryLoading = true;

          state.error = null;

        }
      )

      .addCase(
        getDashboardSummary.fulfilled,
        (state, action) => {

          state.getDashboardSummaryLoading = false;

          state.dashboardSummary =
            action.payload?.data || {};

        }
      )

      .addCase(
        getDashboardSummary.rejected,
        (state, action) => {

          state.getDashboardSummaryLoading = false;

          state.error =
            action.payload ||
            "Failed to fetch dashboard summary";

        }
      )

      /* =================================================
         GET MY TEAM LIST
      ================================================= */

      .addCase(getMyTeamList.pending, (state) => {
        state.myReferralLevelsLoading = true;
        state.myTeamError = null;
      })

      .addCase(getMyTeamList.fulfilled, (state, action) => {
        state.myReferralLevelsLoading = false;

        // API response:
        // { status: 1, data: { totalTeam, activeTeam, levels } }

        state.myReferralLevels = action.payload?.data || {};

        state.myReferralLevelsCount =
          action.payload?.data?.totalTeam || 0;

        state.myTeamError = null;
      })

      .addCase(getMyTeamList.rejected, (state, action) => {
        state.myReferralLevelsLoading = false;

        state.myTeamError =
          action.payload || "Failed to fetch team list";

        state.myReferralLevels = {};
        state.myReferralLevelsCount = 0;
      })


      /* =================================================
         GET DIRECT REFERRALS
      ================================================= */

      .addCase(
        getDirectReferrals.pending,
        (state) => {

          state.directReferralsLoading = true;

          state.error = null;

        }
      )

      .addCase(
        getDirectReferrals.fulfilled,
        (state, action) => {

          state.directReferralsLoading = false;

          const data =
            action.payload?.data || {};


          /* ================================
             SUPPORT MULTIPLE API FORMATS
          ================================= */

          if (Array.isArray(data?.referrals)) {

            state.directReferrals =
              data.referrals;

          } else if (Array.isArray(data)) {

            state.directReferrals =
              data;

          } else if (
            Array.isArray(action.payload?.referrals)
          ) {

            state.directReferrals =
              action.payload.referrals;

          } else {

            state.directReferrals = [];

          }


          /* ================================
             COUNT
          ================================= */

          state.directReferralsCount =
            data?.total ??
            action.payload?.total ??
            state.directReferrals.length;

        }
      )

      .addCase(
        getDirectReferrals.rejected,
        (state, action) => {

          state.directReferralsLoading = false;

          state.error =
            action.payload ||
            "Failed to fetch direct referrals";

          state.directReferrals = [];

          state.directReferralsCount = 0;

        }
      )


      /* =================================================
         GET MY REFERRAL LEVELS
      ================================================= */

      .addCase(
        getMyReferralLevels.pending,
        (state) => {

          state.myReferralLevelsLoading = true;

          state.error = null;

        }
      )

      .addCase(
        getMyReferralLevels.fulfilled,
        (state, action) => {

          state.myReferralLevels =
            action.payload?.data || {};

          state.myReferralLevelsLoading = false;

        }
      )

      .addCase(
        getMyReferralLevels.rejected,
        (state, action) => {

          state.myReferralLevelsLoading = false;

          state.error =
            action.payload ||
            "Failed to fetch referral levels";

          state.myReferralLevels = [];

          state.myReferralLevelsCount = 0;

        }
      )


      /* =================================================
         GET QR
      ================================================= */

      .addCase(
        getQr.pending,
        (state) => {

          state.qrLoading = true;

          state.error = null;

        }
      )

      .addCase(
        getQr.fulfilled,
        (state, action) => {

          state.qrLoading = false;

          /*
           * Expected API:
           *
           * {
           *   status: 1,
           *   data: {
           *     _id,
           *     image,
           *     isActive,
           *     updatedAt
           *   }
           * }
           */

          state.qrData =
            action.payload?.data || null;

        }
      )

      .addCase(
        getQr.rejected,
        (state, action) => {

          state.qrLoading = false;

          state.error =
            action.payload ||
            "Failed to fetch QR";

          state.qrData = null;

        }
      )


      /* =================================================
         UPDATE PROFILE
      ================================================= */

      .addCase(
        updateProfile.pending,
        (state) => {

          state.loading = true;

          state.error = null;

          state.updateSuccess = false;

        }
      )

      .addCase(
        updateProfile.fulfilled,
        (state, action) => {

          state.loading = false;

          state.error = null;

          state.updateSuccess = true;


          /*
           * Merge returned profile data.
           *
           * This is important for walletAddress.
           *
           * Example:
           *
           * walletAddress: ""
           *
           * will correctly replace the
           * previous wallet address.
           */

          if (action.payload?.data) {

            state.profilelist = {

              ...state.profilelist,

              ...action.payload.data,

            };

          }

        }
      )

      .addCase(
        updateProfile.rejected,
        (state, action) => {

          state.loading = false;

          state.updateSuccess = false;

          state.error =
            action.payload ||
            "Failed to update profile";

        }
      )


      /* =================================================
         CHANGE PASSWORD
      ================================================= */

      .addCase(
        changePassword.pending,
        (state) => {

          state.loading = true;

          state.error = null;

        }
      )

      .addCase(
        changePassword.fulfilled,
        (state, action) => {

          state.loading = false;

          state.error = null;

          /*
           * Update profile only if API
           * returns profile data.
           */

          if (action.payload?.data) {

            state.profilelist =
              action.payload.data;

          }

        }
      )

      .addCase(
        changePassword.rejected,
        (state, action) => {

          state.loading = false;

          state.error =
            action.payload ||
            "Failed to change password";

        }
      )


      /* =================================================
         SUBMIT JOINING PAYMENT
      ================================================= */

      .addCase(
        submitJoiningPayment.pending,
        (state) => {

          state.joiningPaymentLoading = true;

          state.joiningPaymentSuccess = false;

          state.error = null;

        }
      )

      .addCase(
        submitJoiningPayment.fulfilled,
        (state, action) => {

          state.joiningPaymentLoading = false;

          state.joiningPaymentSuccess = true;

          state.error = null;

          state.joiningPaymentData =
            action.payload?.data || null;

        }
      )

      .addCase(
        submitJoiningPayment.rejected,
        (state, action) => {

          state.joiningPaymentLoading = false;

          state.joiningPaymentSuccess = false;

          state.error =
            action.payload ||
            "Failed to submit joining payment";

        }
      );

  },

});


/* =====================================================
   ACTIONS
===================================================== */

export const {
  resetUpdateStatus,
  resetJoiningPaymentStatus,
  clearProfileError,
} = profileSlice.actions;


/* =====================================================
   REDUCER
===================================================== */

export default profileSlice.reducer;